import React, { forwardRef, memo, useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { generateFields } from "./renderField";
import { FUNCTION_IDENTIFIER } from "./constants";
import { isNumber } from "./utils";
import GroupForm from "./renderField/GroupForm";
import ChildForm from "./renderField/ChildForm";
import PermissionGuard from "../common/PermissionGuard";

const Index = forwardRef(
  ({ formConfig, onSubmit, buttonName = "Submit", isSearch, loading, permissions }, ref) => {
    const {
      control,
      handleSubmit,
      watch,
      setValue,
      setError,
      clearErrors,
      getValues,
      formState: { errors },
    } = useForm({
      defaultValues: formConfig?.defaultValues || {},
    });

    useImperativeHandle(ref, () => ({
      handleFormSubmit: handleSubmit(onSubmit),
    }));

    const renderField = (field, defaultValues = {}) => {
      const { name } = field;
      const error = errors[name]?.message;

      return generateFields(
        field,
        control,
        error,
        defaultValues[field?.name],
        setValue,
        getValues,
        field?.variant
      );
    };

    const fetchObjectByName = (name, data) => {
      for (let row of data) {
        if (
          row.name === name &&
          row?.functionChangeBaseOn === name &&
          row?.functionMethod === FUNCTION_IDENTIFIER.ON_CHANGE
        ) {
          return row;
        } else if (row?.children) {
          const found = fetchObjectByName(name, row.children);
          if (found) return found;
        }
      }
      return null;
    };

    const handleDynamicChangeCall = async (value, name) => {
      const nameToArray = name.split(".");
      let item;
      let activeIndex;

      if (nameToArray?.length > 1) {
        const parent = nameToArray[0];
        let lastName = nameToArray?.at(-1);

        const parentSchema = formConfig.fields?.find(
          (row) => row.name === parent
        );

        if (parentSchema?.children) {
          if (isNumber(lastName)) {
            lastName = nameToArray?.at(-2)
          }

          item = parentSchema.children.find(
            (child) =>
              child.name === lastName &&
              child.functionChangeBaseOn === lastName &&
              child.functionMethod === FUNCTION_IDENTIFIER.ON_CHANGE
          );
          if (nameToArray?.length > 2) {
            activeIndex = nameToArray[1];
          }
        }
      } else {
        item = fetchObjectByName(name, formConfig?.fields);
      }

      if (item?.customFunction) {
        const latestValue = { ...value };
        if (activeIndex) latestValue.activeIndex = activeIndex;
        item.customFunction(latestValue, setValue, setError, clearErrors);
      }
    };

    useEffect(() => {
      const subscription = watch((value, { name, type }) => {
        if (!name) return;
        handleDynamicChangeCall(value, name, type);
      });
      return () => subscription.unsubscribe();
    }, [watch]);

    return (
      <div>
        <div className="row">
          {formConfig?.fields.map((field) => {
            if (
              field?.isGroupField &&
              field?.children &&
              Array.isArray(field?.children) &&
              field?.children?.length > 0
            ) {
              return (
                <GroupForm
                  key={field.name}
                  input={field}
                  control={control}
                  defaultValues={formConfig?.defaultValues}
                  setValue={setValue}
                  errors={errors}
                  getValues={getValues}
                />
              );
            } else if (
              field?.children &&
              Array.isArray(field?.children) && field?.isMoreRow
            ) {
              return (
                <ChildForm
                  input={field}
                  control={control}
                  defaultValues={formConfig?.defaultValues}
                  setValue={setValue}
                  errors={errors}
                  getValues={getValues}
                />
              );
            }

            return renderField(field, formConfig?.defaultValues);
          })}
          {isSearch && (
            <div className={`form-group mb-3 col-sm-12 col-md-${isSearch}`}>
              <button
                type="button"
                className="btn btn-primary float-end"
                onClick={handleSubmit(onSubmit)}
                style={{ marginTop: "25px", width: "100%" }}
              >
                {buttonName}
              </button>
            </div>
          )}
        </div>
        {!ref && !isSearch && (
          <PermissionGuard permissions={permissions} >
            <button
              type="button"
              className="btn btn-primary float-end"
              onClick={handleSubmit(onSubmit)}
              style={{ marginBottom: "10px" }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : buttonName}
            </button>
          </PermissionGuard>
        )}
      </div>
    );
  }
);

export default memo(Index);
